import { ComponentNames } from "../../constant/component-constant";
import { arraytoObj, cleanEmpty } from "../../helper/sb-fixer";
import { logError } from "../../helper/logger";

interface IDtvCompetitorComparisonTableAdapterArgs {
  content: any;
  competitorComparison: any;
  genreSvodOffers: any;
  packageConfig?: any;
}

const ADAPTER_NAME_NAME = "dtvCompetitorComparisonTableAdapter";

// Generated by Copilot- Start - 2026-03-13 12:39:42
/**
 * Builds a lookup map from packageConfig queryparam (lowercased) to the
 * corresponding offerPrices entry's id, so we can resolve CMS package keys
 * (e.g. "myKids") to offer-price keys (e.g. "ATT_TV_MYKIDS").
 */
const buildQueryParamToOfferKeyMap = (
  packageConfig?: any[]
): Record<string, string> => packageConfig?.reduce((map, pkg) => {
  if (pkg?.queryparam && pkg?.id) {
    map[pkg?.queryparam?.toLowerCase()] = pkg?.id;
  }
  return map;
}, {} as Record<string, string>) ?? {};

/**
 * Converts the offerPrices array (each element is a single-key object) into
 * a flat lookup: { "ATT_TV_MYKIDS": { price, regularPrice }, ... }
 */
const buildOfferPricesMap = (
  offerPrices: any[]
): Record<string, { price?: number; regularPrice?: number }> => offerPrices?.reduce((map, entry) => {
  const key = Object.keys(entry ?? {})?.[0];
  if (key) {
    map[key] = entry[key];
  }
  return map;
}, {} as Record<string, { price?: number; regularPrice?: number }>) ?? {};

// Generated by Copilot- Start - 2026-03-19 17:53:03
/**
 * Replaces [strikeout_price] and [offer_price] placeholders inside
 * genrePack.mainPackages.monthlyCost with dynamic EPOCH prices.
 *
 * Rules:
 *   1. No placeholders → keep CMS string as-is.
 *   2. Placeholders exist but EPOCH prices unavailable → keep CMS string as-is.
 *   3. Both placeholders, prices equal → plain string (offer price only, no strikeout).
 *   3a. Both placeholders, one price missing → plain string (available price only, no strikeout).
 *   4. Both placeholders, prices differ → { strikeoutText, offerText } object for UI.
 *   5. Single placeholder → simple string replacement.
 *
 * CMS template: "$[strikeout_price]/mo. $[offer_price]/mo."
 *
 * For dual-price split, we find the last "$" before [offer_price]
 * to cleanly separate strikeout vs offer portions.
 */
const applyDynamicMonthlyCost = (
  competitorComparison: any,
  offerPricesMap: Record<string, { price?: number; regularPrice?: number }>,
  queryParamToOfferKey: Record<string, string>
): any => {
  if (!competitorComparison?.competitorTableBodyProps?.genrePack?.mainPackages) {
    return competitorComparison ? JSON.parse(JSON.stringify(competitorComparison)) : competitorComparison;
  }

  const updated = JSON.parse(JSON.stringify(competitorComparison));
  const mainPackages = updated?.competitorTableBodyProps?.genrePack?.mainPackages;

  Object.keys(mainPackages).forEach(pkgKey => {
    const pkg = mainPackages?.[pkgKey];
    if (!pkg || typeof pkg !== "object" || !Object.prototype.hasOwnProperty.call(pkg, "monthlyCost")) {
      return;
    }

    const currentCost = pkg?.monthlyCost;
    if (typeof currentCost !== "string") {
      return;
    }

    const hasStrikeout = currentCost?.includes("[strikeout_price]");
    const hasOffer = currentCost?.includes("[offer_price]");

    // Rule 1: No placeholders → keep CMS value as-is
    if (!hasStrikeout && !hasOffer) {
      return;
    }

    const offerKey = queryParamToOfferKey?.[pkgKey.toLowerCase()];
    const regularPrice = offerKey != null ? offerPricesMap?.[offerKey]?.regularPrice : undefined;
    const offerPrice = offerKey != null ? offerPricesMap?.[offerKey]?.price : undefined;

    // Rule 2: EPOCH prices unavailable → keep CMS value unchanged
    if (regularPrice == null && offerPrice == null) {
      return;
    }

    // Rule 5: Single placeholder — simple replacement
    if (!hasStrikeout || !hasOffer) {
      let resolved = currentCost;
      if (hasStrikeout && regularPrice != null) {
        resolved = resolved?.replace("[strikeout_price]", regularPrice?.toFixed(2));
      }
      if (hasOffer && offerPrice != null) {
        resolved = resolved?.replace("[offer_price]", offerPrice?.toFixed(2));
      }
      mainPackages[pkgKey].monthlyCost = resolved;
      return;
    }

    // --- Both placeholders present ---
    // Find the "$" before [offer_price] to split strikeout vs offer portions
    // e.g. "$[strikeout_price]/mo. $[offer_price]/mo." → split at second "$"
    const offerIdx = currentCost?.indexOf("[offer_price]");
    const currencyIdx = currentCost?.lastIndexOf("$", offerIdx);
    const splitIdx = currencyIdx > 0 ? currencyIdx : offerIdx;

    const strikeoutPart = currentCost?.substring(0, splitIdx).trim();
    const offerPart = currentCost?.substring(splitIdx);

    // Rule 3a: Either price missing → show only the available price as plain string
    // (avoids broken strikeout like "$/mo." when regularPrice is absent)
    if (regularPrice == null || offerPrice == null) {
      const available = offerPrice ?? regularPrice;
      mainPackages[pkgKey].monthlyCost = available != null ?
        (offerPrice != null ?
          offerPart?.replace("[offer_price]", available.toFixed(2))?.trim() :
          strikeoutPart?.replace("[strikeout_price]", available.toFixed(2))?.trim()) :
        currentCost;
      return;
    }

    // Rule 3: Prices equal → plain string (offer portion only, no strikeout)
    if (regularPrice === offerPrice) {
      mainPackages[pkgKey].monthlyCost = offerPart?.replace("[offer_price]", offerPrice?.toFixed(2))?.trim();
      return;
    }

    // Rule 4: Prices differ → structured object for UI
    const strikeoutText = strikeoutPart?.replace("[strikeout_price]", regularPrice.toFixed(2));
    const offerText = offerPart?.replace("[offer_price]", offerPrice.toFixed(2))?.trim();

    mainPackages[pkgKey].monthlyCost = {
      strikeoutText,
      offerText
    };
  });

  return updated;
};
// Generated by Copilot- End - 2026-03-19 17:53:03

export const dtvCompetitorComparisonTableAdapter = ({
  content: componentProps,
  competitorComparison: competitorComparison,
  genreSvodOffers: genreSvodOffers,
  packageConfig: packageConfig
}: IDtvCompetitorComparisonTableAdapterArgs): any => {
  try {
    const offerPrices = genreSvodOffers?.offerPrices || [];

    // Generated by Copilot- Start - 2026-03-07 00:14:16
    const offerPricesMap = buildOfferPricesMap(offerPrices);
    const queryParamToOfferKey = buildQueryParamToOfferKeyMap(packageConfig);

    const updatedCompetitorComparison = applyDynamicMonthlyCost(
      competitorComparison,
      offerPricesMap,
      queryParamToOfferKey
    );
    // Generated by Copilot- End - 2026-03-07 00:14:16
    const content = cleanEmpty(arraytoObj(componentProps));
    return {
      props: {
        ...content,
        ...(content.options && { gnavOptions: content.options }),
        options: {
          id: content.id || null,
          ...(content.options && { ...content.options })
        },
        competitorComparison: updatedCompetitorComparison
      },
      type: ComponentNames.COMPETITORCOMPARISONTABLEV2
    };
  } catch (error) {
    logError(ADAPTER_NAME_NAME, error, {});
  }
};

export default dtvCompetitorComparisonTableAdapter;
